import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScrollRestoration } from "use-scroll-restoration";
import { THEME_COLORS } from "../themes/DarkLight";
import useThemeHook from "../hooks/useThemeHook";
import Observer from "./Observer";
import { isNotNullOrUndefined } from "../utils/isNotNullOrUndefined";
import useGlobalRefreshLogicHook from "../hooks/useGlobalRefreshLogicHook";
import isRunningOnMobile from "../utils/isRunningOnMobile";
import { actions } from "../redux/index";
import LoadingIndicator from "../components/LoadingIndicator";
const FlatList = ({
  listFooterComponents,
  listHeaderComponents,
  renderItems,
  data,
  pageKey,
  onRefresh,
  itemsClassName,
  itemIdExtractor,
  onEndReached,
  reversed = false,
  startAtBottom = false,
  blockScrolling = false,
  horizontal = false,
}: {
  startAtBottom?: boolean;
  reversed?: boolean;
  listFooterComponents?: React.ReactElement;
  listHeaderComponents?: React.ReactElement;
  renderItems: Function;
  data: any[];
  horizontal?: boolean;
  pageKey: string;
  onRefresh: Function;
  itemsClassName: string;
  itemIdExtractor: Function;
  onEndReached: Function;
  blockScrolling?: boolean;
}) => {
  var reduxVisibleElement = useSelector((state: any) => {
    return state?.flatlistPageVisibleElement?.[itemsClassName]?.[pageKey];
  });
  useEffect(() => {
    if (isRunningOnMobile()) {
      const element = document.getElementById(reduxVisibleElement);

      if (element) {
        element.scrollIntoView({ behavior: "auto", block: "center" });
      }
    }
  }, []);
  const { ref } = useScrollRestoration(pageKey ?? "", {
    debounceTime: 200,
    persist: "sessionStorage",
  });

  const dispatch = useDispatch();
  const { isRefreshing, containerRef, handleTouchStart } =
    useGlobalRefreshLogicHook({ onRefresh: onRefresh });

  const handleTouchEnd = (e: any) => {
    try {
      setTimeout(() => {
        // console.log(e.target.closest("div.Post"));
        if (e.target.closest(itemsClassName)?.getAttribute("id") != null) {
          dispatch(
            actions.updateFlatlistVisibleElement({
              pageKey,
              class: itemsClassName,
              element: e.target.closest(itemsClassName)?.getAttribute("id"),
            })
          );
        }
      }, 1000);
    } catch (error) {}
  };
  const theme = useThemeHook();
  const [startedAtBottomInitialized, setStartedAtBottomInitialized] =
    useState(false);
  const trackListEnd = (elementIndex: number) => {
    if (startAtBottom && !startedAtBottomInitialized) {
      return;
    }
    const lastIndex = reversed ? 0 : (data?.length ?? 0) - 1;
    if (
      itemIdExtractor(data[elementIndex]) === itemIdExtractor(data[lastIndex])
    ) {
      onEndReached();
    }
  };

  const scrollToBottom = () => {
    if (
      startAtBottom &&
      isNotNullOrUndefined(data) &&
      data.length !== 0 &&
      !startedAtBottomInitialized
    ) {
      document
        .getElementById("scroll-to-bottom-id")
        ?.scrollIntoView({ behavior: "auto", block: "start" });
      setStartedAtBottomInitialized(true);
      console.log("bottom initialized");
    }
  };

  useEffect(() => {
    // Scroll to the bottom when the component is mounted
    if (startAtBottom) {
      scrollToBottom();
    }
  }, [data]); // Empty dependency array ensures it only runs once on mount

  return (
    <div
      onTouchEnd={(e) => handleTouchEnd(e)}
      ref={
        isNotNullOrUndefined(pageKey)
          ? isRunningOnMobile()
            ? containerRef
            : ref
          : undefined
      }
      onTouchStart={(e) => handleTouchStart(e)}
      style={{
        width: "100%",
        height: isNotNullOrUndefined(pageKey)
          ? isRunningOnMobile()
            ? "100%"
            : "100vh"
          : "100%",
        overflow: blockScrolling ? "hidden" : "auto",
        backgroundColor: THEME_COLORS[theme].full_theme,
        flexDirection: reversed ? "column-reverse" : "column",
      }}
    >
      {isRefreshing && <LoadingIndicator />}
      {reversed ? listFooterComponents : listHeaderComponents}
      {(reversed ? [...data].reverse() : data).map((item, index) => (
        <Observer index={index} onVisible={trackListEnd}>
          {renderItems({ item, index })}
        </Observer>
      ))}
      {reversed ? listHeaderComponents : listFooterComponents}
      <div id="scroll-to-bottom-id"></div>
    </div>
  );
};

export default memo(FlatList);
